"use client";
import { useEffect, useState, createContext, useContext } from 'react';
import { usePathname } from 'next/navigation';

// Create context to share Lenis instance
const LenisContext = createContext(null);

export const useLenis = () => {
  return useContext(LenisContext);
};

export default function LenisProvider({ children }) {
  const [lenis, setLenis] = useState(null);
  const pathname = usePathname();

  useEffect(() => {
    const initLenis = async () => {
      // Only run on client side to avoid SSR issues
      if (typeof window === 'undefined') return;
      
      try {
        const { default: Lenis } = await import('lenis');
      
      const lenisInstance = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 2,
        infinite: false,
        autoResize: true,
        syncTouch: false,
        touchInertiaMultiplier: 35,
        // Opt-in only: blanket-preventing INPUT/TEXTAREA made wheel events
        // over form fields scroll natively while Lenis kept easing toward
        // its own target, so the page rubber-banded over the contact form.
        prevent: (node) =>
          node.hasAttribute?.('data-lenis-prevent') ||
          !!node.closest?.('[data-lenis-prevent]'),
      });

      setLenis(lenisInstance);

      // Handle anchor links with proper offset for header
      lenisInstance.on('scroll', ({ scroll, limit, velocity, direction, progress }) => {
        // Update any scroll-dependent animations here if needed
      });

      // Animation frame loop
      function raf(time) {
        lenisInstance.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);

      // Handle anchor scrolling with offset
      const handleAnchorClick = (e) => {
        const link = e.target.closest('a');
        if (!link || !link.href) return;

        const url = new URL(link.href);
        const hash = url.hash;
        
        // Only handle same-origin links with anchors, but exclude footnote links
        if (url.origin === window.location.origin && hash) {
          // Skip footnote navigation - let browser handle it natively
          if (hash.includes('user-content-fn') || hash.includes('user-content-fnref')) {
            return;
          }
          
          const target = document.querySelector(hash);
          if (target) {
            e.preventDefault();
            
            // Calculate offset for fixed header (112px = 28 * 4 in Tailwind)
            const headerOffset = 112;
            const targetPosition = target.offsetTop - headerOffset;
            
            if (url.pathname !== window.location.pathname) {
              // Cross-page navigation: navigate first, then scroll
              window.history.pushState(null, '', link.href);
              setTimeout(() => {
                lenisInstance.scrollTo(targetPosition, { duration: 1.5 });
              }, 100);
            } else {
              // Same page: scroll immediately
              lenisInstance.scrollTo(targetPosition, { duration: 1.5 });
            }
          }
        }
      };

      document.addEventListener('click', handleAnchorClick);

      // Handle direct hash navigation (e.g., page reload with hash)
      if (window.location.hash) {
        setTimeout(() => {
          const target = document.querySelector(window.location.hash);
          if (target) {
            const headerOffset = 112;
            const targetPosition = target.offsetTop - headerOffset;
            lenisInstance.scrollTo(targetPosition, { duration: 1.5, immediate: false });
          }
        }, 500); // Delay to ensure layout is stable
      }

      return () => {
        document.removeEventListener('click', handleAnchorClick);
        lenisInstance.destroy();
      };
      } catch (error) {
        console.warn('Failed to initialize Lenis:', error);
        return () => {}; // Return empty cleanup function
      }
    };

    const cleanup = initLenis();
    
    return () => {
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(cleanupFn => cleanupFn && cleanupFn());
      }
    };
  }, []);

  // Handle route changes for cross-page navigation
  useEffect(() => {
    if (typeof window === 'undefined' || !lenis) return;
    
    // Scroll to top on route change (without hash)
    if (!window.location.hash) {
      lenis.scrollTo(0, { immediate: true });
    } else {
      // Handle hash navigation
      setTimeout(() => {
        const target = document.querySelector(window.location.hash);
        if (target) {
          const headerOffset = 112;
          const targetPosition = target.offsetTop - headerOffset;
          lenis.scrollTo(targetPosition, { duration: 1.5 });
        }
      }, 500);
    }
  }, [pathname, lenis]);

  return (
    <LenisContext.Provider value={lenis}>
      {children}
    </LenisContext.Provider>
  );
}